import React, { useCallback, useMemo, useState } from "react";
import { Circle, Marker, Polygon, Polyline, Tooltip } from "react-leaflet";
import { Icon, Marker as LMarker } from "leaflet";
import MapIconPin from "../../../assets/icons/MapIconPin.svg";
import { GPS, TrapSite } from "../../../types";
import { fromLatLng, toLatLng } from "../../../utils/geo";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";
import { EditorDevice } from "./types";
import { radiusOf } from "./trapProximity";
import {
  BOUNDARY_STYLE,
  OVERLAP_REGION_STYLE,
  SHOW_COVERAGE_SHADING,
} from "./constants";
import { BoundaryCircle, overlapRegions } from "./boundaries";

// Same geometry as the real pins (makeTrapIcon in MarkerManager), so a draft
// sits exactly where the saved marker will.
const icon = (className: string) =>
  new Icon({
    iconUrl: MapIconPin,
    iconAnchor: [15, 40],
    className: `w-10 h-10 translate-y-[20px] z-1000 ${className}`,
  });

const PRIMARY_ICON = icon("trapsite-draft-pin");
const SITE_ICON = icon("trapsite-secondary-pin");
const DEVICE_ICON = icon("trapsite-device-pin");
const GHOST_ICON = icon("trapsite-ghost-pin");

/**
 * The pin graphic is shifted down 20px from its anchor, so a "top" tooltip
 * would otherwise land in the middle of it. Clears the artwork entirely.
 */
const LABEL_OFFSET: [number, number] = [0, -26];

/** Ghost labels hang below, so they never collide with the live pin's own. */
const GHOST_LABEL_OFFSET: [number, number] = [0, 24];

interface Props {
  /** The pin being edited. Always draggable. */
  primary: GPS;
  onMovePrimary: (gps: GPS) => void;
  /** Any pin finished moving; the view refits around the new arrangement. */
  onMoveEnd: () => void;
  /** Other devices on screen, draggable, staged rather than saved directly. */
  devices: EditorDevice[];
  /** Collection locations shown as draggable pins. */
  trapSites: Partial<TrapSite>[];
  /**
   * Every collection location, drawn as a boundary only.
   *
   * Context rather than clutter: an overlap needs something visible to
   * overlap with, so widening a radius until it reaches a neighbour has to
   * show that neighbour's circle for the red patch to mean anything.
   */
  contextSites?: Partial<TrapSite>[];
  /** The location being edited, whose circle comes from the live draft. */
  primaryTrapSiteId?: string | null;
  /**
   * The pin being edited is a collection location, and this is its radius -
   * so its boundary is drawn from the live draft rather than the stored row.
   */
  primaryRadiusM?: number | null;
  /** Name shown against the pin being edited. */
  primaryLabel?: string | null;
  /** Where the pin being edited was recorded, for the drag line. */
  primaryOrigin?: GPS | null;
  /** Where things sat before they were moved, drawn faintly behind. */
  ghosts?: Array<{ key: string; gps: GPS; label?: string }>;
}

/**
 * Every pin the editor puts on the map.
 *
 * The distinction between primary and secondary is only which one the form is
 * about - both are draggable, because correcting a trap's position often means
 * nudging the location it belongs to at the same time.
 */
const EditorMarkers: React.FC<Props> = ({
  primary,
  onMovePrimary,
  onMoveEnd,
  devices,
  trapSites,
  contextSites = [],
  primaryTrapSiteId,
  primaryRadiusM,
  primaryLabel,
  primaryOrigin,
  ghosts = [],
}) => {
  const editor = useTrapsiteEditorContext();

  /**
   * Which pin is under the cursor, and where it now is.
   *
   * Pure feedback - it lasts exactly as long as the drag. The origin is NOT
   * captured here: the line is drawn back to where the thing was recorded, so
   * dragging twice still measures from the original position rather than from
   * the last drop.
   */
  const [dragging, setDragging] = useState<{ key: string; to: GPS } | null>(
    null,
  );

  const trackDrag = useCallback(
    (key: string, e: any) =>
      setDragging({ key, to: fromLatLng(e.target.getLatLng()) }),
    [],
  );
  const endDrag = useCallback(() => setDragging(null), []);

  /**
   * Where each pin was recorded, before anything was dragged.
   *
   * `devices` and `trapSites` arrive carrying their stored positions - the
   * staged overrides are applied at render - so these are the untouched ones.
   */
  const originFor = useCallback(
    (key: string): GPS | null => {
      if (key === "primary") return primaryOrigin ?? null;
      if (key.startsWith("device-")) {
        return devices.find((d) => `device-${d.uuid}` === key)?.gps ?? null;
      }
      return trapSites.find((t) => `site-${t._id}` === key)?.gps ?? null;
    },
    [devices, trapSites, primaryOrigin],
  );

  const dragOrigin = dragging ? originFor(dragging.key) : null;

  const primaryHandlers = useMemo(
    () => ({
      drag: (e: { target: LMarker }) => {
        onMovePrimary(fromLatLng(e.target.getLatLng()));
        trackDrag("primary", e);
      },
      dragend: (e: { target: LMarker }) => {
        onMovePrimary(fromLatLng(e.target.getLatLng()));
        endDrag();
        onMoveEnd();
      },
    }),
    [onMovePrimary, onMoveEnd, trackDrag, endDrag],
  );

  /**
   * Every boundary on screen, at the position and radius it is drawn at.
   *
   * Built from the live draft and any staged drags, so widening a radius or
   * dragging a location into its neighbour changes what is shared as it
   * happens rather than after a save.
   */
  const boundaryCircles = useMemo(() => {
    if (!SHOW_COVERAGE_SHADING) return [];

    const circles: BoundaryCircle[] = [];

    if (primaryRadiusM != null) {
      circles.push({
        key: "primary",
        center: primary,
        radiusM: primaryRadiusM,
      });
    }

    const seen = new Set<string>();
    for (const site of [...trapSites, ...contextSites]) {
      const id = site._id;
      // The location being edited is already the primary circle; drawing the
      // stored one too would leave a stale ring behind as it is dragged.
      if (!id || id === primaryTrapSiteId || seen.has(id)) continue;
      seen.add(id);

      const gps = editor.staged.trapSites[id] ?? site.gps;
      if (gps) {
        circles.push({
          key: `site-${id}`,
          center: gps,
          radiusM: radiusOf(site),
        });
      }
    }

    return circles;
  }, [
    primary,
    primaryRadiusM,
    primaryTrapSiteId,
    trapSites,
    contextSites,
    editor.staged,
  ]);

  const overlaps = useMemo(
    () => overlapRegions(boundaryCircles),
    [boundaryCircles],
  );

  return (
    <>
      {/* Where each moved thing was, so a drag is undoable by eye and the
          size of the move is obvious. Cleared when the editor closes, which
          saving does. */}
      {ghosts.map((ghost) => (
        <Marker
          key={`ghost-${ghost.key}`}
          position={toLatLng(ghost.gps)}
          icon={GHOST_ICON}
          interactive={false}
          zIndexOffset={-500}
        >
          <Tooltip
            permanent
            direction="bottom"
            offset={GHOST_LABEL_OFFSET}
            className="editor-pin-label editor-ghost-label"
          >
            {ghost.label ? `${ghost.label} was here` : "Was here"}
          </Tooltip>
        </Marker>
      ))}

      {/* Where this pin started, for as long as it is moving. Solid, unlike
          the standing dashed boundaries, so it reads as a live gesture rather
          than another permanent line on the map. */}
      {dragging && dragOrigin && (
        <Polyline
          positions={[toLatLng(dragOrigin), toLatLng(dragging.to)]}
          pathOptions={{ color: "#00adef", weight: 2, opacity: 0.9 }}
        />
      )}

      {/* What each location claims to cover. Drawn from the same position as
          its pin, so a boundary travels with it as it is dragged rather than
          staying behind at the stored coordinates. */}
      {boundaryCircles.map((circle) => (
        <Circle
          key={`bounds-${circle.key}`}
          center={toLatLng(circle.center)}
          radius={circle.radiusM}
          pathOptions={BOUNDARY_STYLE}
        />
      ))}

      {/* After the circles, so the shared patch reads on top of both outlines
          rather than under one of them. */}
      {overlaps.map((region) => (
        <Polygon
          key={`overlap-${region.key}`}
          positions={region.ring.map(toLatLng)}
          pathOptions={OVERLAP_REGION_STYLE}
        />
      ))}

      {trapSites.map((site) => {
        const gps = editor.staged.trapSites[site._id!] ?? site.gps;
        if (!gps) return null;
        return (
          <Marker
            key={`site-${site._id}`}
            position={toLatLng(gps)}
            icon={SITE_ICON}
            draggable
            zIndexOffset={1000}
            eventHandlers={{
              drag: (e: any) => {
                editor.stageTrapSiteMove(
                  site._id!,
                  fromLatLng(e.target.getLatLng()),
                );
                trackDrag(`site-${site._id}`, e);
              },
              dragend: (e: any) => {
                editor.stageTrapSiteMove(
                  site._id!,
                  fromLatLng(e.target.getLatLng()),
                );
                endDrag();
                onMoveEnd();
              },
            }}
          >
            <Tooltip
              permanent
              direction="top"
              offset={LABEL_OFFSET}
              className="editor-pin-label"
            >
              {site.display_name || "Unnamed location"}
            </Tooltip>
          </Marker>
        );
      })}

      {devices.map((device) => {
        const gps = editor.staged.devices[device.uuid] ?? device.gps;
        if (!gps) return null;
        return (
          <Marker
            key={`device-${device.uuid}`}
            position={toLatLng(gps)}
            icon={DEVICE_ICON}
            draggable
            zIndexOffset={1100}
            eventHandlers={{
              drag: (e: any) => {
                editor.stageDeviceMove(
                  device.uuid,
                  fromLatLng(e.target.getLatLng()),
                );
                trackDrag(`device-${device.uuid}`, e);
              },
              dragend: (e: any) => {
                editor.stageDeviceMove(
                  device.uuid,
                  fromLatLng(e.target.getLatLng()),
                );
                endDrag();
                onMoveEnd();
              },
            }}
          >
            <Tooltip
              permanent
              direction="top"
              offset={LABEL_OFFSET}
              className="editor-pin-label"
            >
              {device.displayName}
            </Tooltip>
          </Marker>
        );
      })}

      <Marker
        position={toLatLng(primary)}
        icon={PRIMARY_ICON}
        draggable
        autoPan
        zIndexOffset={1200}
        eventHandlers={primaryHandlers}
      >
        {primaryLabel && (
          <Tooltip
            permanent
            direction="top"
            offset={LABEL_OFFSET}
            className="editor-pin-label"
          >
            {primaryLabel}
          </Tooltip>
        )}
      </Marker>
    </>
  );
};

export default EditorMarkers;
